import { ObservableMap, action, computed, observable, runInAction } from "mobx";
import QuickPlayStore from "./QuickPlayStore";
import { LoaderStore } from "@state/stores/common";
import { BettingTips } from "@api-types/bet-booster/BettingTipsType";
import { ChangePageButton } from "./MostPlayedTicketsStore";
import { BetBoosterApiService } from "@api-services/bet-booster/BetBoosterApiService";
import { HubState } from "@gp/hub";
import { THEME_SETTINGS } from "@theme-settings";

export default class BettingTipsStore {
	quickPlayStore: QuickPlayStore;
	loader: LoaderStore;

	@observable isPopupDisplayed: boolean = false;
	@observable tips = new ObservableMap<string, BettingTips>();
	@observable selectedTip: BettingTips | null = null;
	@observable currentTipIndex: number;
	@observable isStoreInitialized: boolean = false;
	@observable error: Error | null = null;

	constructor(quickPlayStore: QuickPlayStore) {
		this.loader = new LoaderStore();
		this.quickPlayStore = quickPlayStore;
	}

	@action.bound
	mapData(data: BettingTips[] | null) {
		this.tips.clear();
		data?.forEach((response) => {
			this.tips.set(response.id, response);
		});
	}

	@computed get bettingTips() {
		return Array.from(this.tips.values()) as BettingTips[];
	}

	@computed
	get isLoading(): boolean {
		return this.loader.isLoading;
	}

	@action.bound
	public async fetchBettingTips(params?: {
		eventId?: string;
		tournamentId?: string;
	}) {
		this.loader.suspend();
		try {
			this.tips.clear();
			const response = await BetBoosterApiService.getBettingTips(params);
			runInAction(() => {
				this.mapData(response.response);
			});
		} catch (e) {
			runInAction(() => {
				this.error = e;
				this.tips.clear();
			});
			console.error(e);
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	activatePopup(id: string, index: number) {
		this.selectedTip = this.tips.get(id) || null;
		this.currentTipIndex = index;
		this.isPopupDisplayed = true;
	}

	@action.bound
	deactivatePopup() {
		this.isPopupDisplayed = false;
		this.selectedTip = null;
	}

	@action.bound
	changePage(direction: ChangePageButton) {
		const tips = this.bettingTips;
		if (!tips.length) return;

		let currentIndex = tips.findIndex((tip) => tip === this.selectedTip);

		switch (direction) {
			case ChangePageButton.PREVIOUS:
				currentIndex =
					currentIndex > 0 ? currentIndex - 1 : tips.length - 1;
				break;
			case ChangePageButton.NEXT:
				currentIndex =
					currentIndex < tips.length - 1 ? currentIndex + 1 : 0;
				break;
			default:
				currentIndex = direction;
				break;
		}

		this.selectedTip = tips[currentIndex];
		this.currentTipIndex = currentIndex + 1;
	}

	@action.bound
	async onInitialized(params?: { eventId?: string }) {
		this.tips.clear();
		this.selectedTip = null;
		this.currentTipIndex = 0;
		this.error = null;
		await this.fetchBettingTips(params);
		this.isStoreInitialized = true;
	}

	@action.bound
	onDispose() {
		this.tips.clear();
		this.isPopupDisplayed = false;
		this.selectedTip = null;
		this.isStoreInitialized = false;
		this.currentTipIndex = 0;
		this.error = null;
	}

	@computed get isEmpty() {
		return this.bettingTips.length === 0;
	}
}
